import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Phone, MessageCircle, Check } from "lucide-react";

// Images
import deluxe1 from "../assets/images/deluxe1.JPG";

import executive1 from "../assets/images/executive1.jpeg";
import executive2 from "../assets/images/executive2.jpeg";

import premium1 from "../assets/images/premium1.jpeg";
import premium2 from "../assets/images/premium2.jpeg";
import premium3 from "../assets/images/premium3.jpeg";

export default function RoomCategoriesPage() {
  const [currentIndexes, setCurrentIndexes] = useState({});
  const [isPaused, setIsPaused] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleInquiry = () => {
    window.open(
      "https://wa.me/919187501932?text=Hello, I'd like to inquire about room availability"
    );
  };
  const rooms = useMemo(() => [
    {
      title: "Deluxe Suite",
      subtitle: "Single Room + Bathroom + Balcony",
      description:
        "Ideal for solo guests, students, and professionals seeking a comfortable and affordable short stay.",
      images: [deluxe1],
      features: [
        "Spacious hall with comfortable seating",
        "Wardrobe and Storage Space",
        "Private attached bathroom",
      ],
    },
    {
      title: "Executive Suite",
      subtitle: "Hall + Bed Room + Bathroom + Balcony",
      description:
        "Recommended for couples, hospital visitors, and small families looking for extra space and comfort.",
      images: [executive1, executive2],
      features: [
        "Comfortable single/double bed",
        "Separate bedroom for privacy",
        "Spacious hall area",
        "Clean and modern attached bathroom",
        "Wardrobe and storage space",
      ],
    },
    {
      title: "Premium 1BHK Suite",
      subtitle: "Kitchen + Hall + Bed Room + Bathroom + Balcony",
      description:
        "Best suited for families, long-term guests, and those who prefer a fully equipped apartment with kitchen facilities.",
      images: [premium1, premium2, premium3],
      features: [
        "Fully equipped kitchen with gas stove",
        "Ideal for cooking your own meals",
        "Separate bedroom for privacy",
        "Spacious hall area",
        "Large attached bathroom",
        "Wardrobe and storage space",
      ],
    },
  ], []);
  const nextSlide = (roomIndex, total) => {
    if (total <= 1) return;

    setCurrentIndexes((prev) => ({
      ...prev,
      [roomIndex]: ((prev[roomIndex] || 0) + 1) % total,
    }));
  };

  const prevSlide = (roomIndex, total) => {
    if (total <= 1) return;

    setCurrentIndexes((prev) => ({
      ...prev,
      [roomIndex]: ((prev[roomIndex] || 0) - 1 + total) % total,
    }));
  };

  // Auto Slide
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      rooms.forEach((room, index) => {
        nextSlide(index, room.images.length);
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, rooms]);
  // Swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = (index, total) => {
    const distance = touchStartX.current - touchEndX.current;

    if (distance > 50) nextSlide(index, total);
    if (distance < -50) prevSlide(index, total);
  };



  return (
    <div className="min-h-screen py-16 bg-gradient-to-b from-white to-[#FFF8DC]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#8B6F47] mb-4">
            Room Categories
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our comfortable and well-maintained rooms designed for your convenience
          </p>
        </div>

        <div className="space-y-12 max-w-6xl mx-auto">
          {rooms.map((room, index) => (
            <Card
              key={index}
              className="overflow-hidden border-2 border-[#E6D5C3] hover:shadow-xl transition-all duration-300"
            >
              <div className={`grid md:grid-cols-2 ${index % 2 === 1 ? "md:grid-flow-dense" : ""}`}>

                {/* Image Slider */}
                <div
                  className={`relative h-80 md:h-auto overflow-hidden select-none ${index % 2 === 1 ? "md:col-start-2" : ""
                    }`}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => handleTouchEnd(index, room.images.length)}
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                >
                  <div
                    className="flex h-full transition-transform duration-300 ease-in-out"
                    style={{
                      transform: `translateX(-${(currentIndexes[index] || 0) * (100 / room.images.length)}%)`,
                      width: `${room.images.length * 100}%`,
                    }}
                  >
                    {room.images.map((img, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="flex-shrink-0"
                        style={{ width: `${100 / room.images.length}%` }}
                      >
                        <img
                          src={img}
                          alt={`${room.title}-${imgIndex}`}
                          className="w-full h-80 md:h-full object-cover"
                          draggable="false"
                        />
                      </div>
                    ))}
                  </div>

                  {room.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => prevSlide(index, room.images.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white p-3 rounded-full"
                      >
                        ❮
                      </button>

                      <button
                        type="button"
                        onClick={() => nextSlide(index, room.images.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white p-3 rounded-full"
                      >
                        ❯
                      </button>

                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                        {room.images.map((_, imgIndex) => (
                          <button
                            key={imgIndex}
                            type="button"
                            onClick={() =>
                              setCurrentIndexes((prev) => ({
                                ...prev,
                                [index]: imgIndex,
                              }))
                            }
                            className={`w-2.5 h-2.5 rounded-full ${(currentIndexes[index] || 0) === imgIndex
                              ? "bg-white"
                              : "bg-white/50"
                              }`}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                </div>

                {/* Content */}
                <div className="p-8 md:p-10 flex flex-col justify-center bg-white">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="text-3xl font-serif text-[#8B6F47] mb-2">
                      {room.title}
                    </CardTitle>
                    <p className="text-[#A0826D] font-medium text-lg">{room.subtitle}</p>
                  </CardHeader>

                  <CardContent className="p-0">
                    <p className="text-gray-700 mb-6 text-base leading-relaxed">
                      {room.description}
                    </p>

                    <div className="space-y-3 mb-8">
                      {room.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-[#8B6F47] mt-0.5" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => window.open("tel:+919187501932")}
                        className="bg-[#8B6F47] hover:bg-[#6D5838] text-white flex-1"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call to Inquire
                      </Button>

                      <Button
                        onClick={handleInquiry}
                        variant="outline"
                        className="border-2 border-[#8B6F47] text-[#8B6F47] hover:bg-[#8B6F47] hover:text-white flex-1"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </div>

              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}